import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { scoreColor } from "@/components/ScoreMeter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wand2, Loader2, FolderPlus, Hammer, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SELECTS = {
  industry: ["AI", "FinTech", "Healthcare", "Real Estate", "Restaurants", "Creator Economy", "Developer Tools", "E-commerce", "Any"],
  business_model: ["Subscription", "Marketplace", "Usage-based", "Freemium", "Any"],
  customer_type: ["B2B", "B2C", "SMB", "Enterprise", "Prosumer"],
  approach: ["Code", "No-code", "Hybrid"],
  ai_preference: ["AI-first", "AI-optional", "No AI"],
};

export default function IdeaGenerator() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ industry: "Any", skills: "", budget: "", build_time: "", business_model: "Subscription", target_revenue: "", customer_type: "B2B", ai_preference: "AI-first", approach: "Hybrid" });
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const pick = (k, v) => setForm({ ...form, [k]: v });

  const generate = async () => {
    setLoading(true); setIdeas([]);
    try {
      const res = await api.post("/ai/generate-ideas", { ...form, count });
      setIdeas(res.data.ideas || []);
      toast.success(`${res.data.ideas?.length || 0} opportunities generated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const toProject = async (idea) => {
    try { await api.post("/projects", { title: idea.title }); toast.success("Added to workspace"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center mx-auto mb-4"><Wand2 className="w-6 h-6 text-indigo-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">Find My Next SaaS</h1>
        <p className="text-slate-400 mt-2 max-w-xl mx-auto">Tell us your constraints. Our Opportunity Agent generates ranked, buildable opportunities tailored to you.</p>
      </div>

      <div className="glass rounded-2xl p-6 sm:p-8 mb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Object.entries(SELECTS).map(([key, opts]) => (
            <div key={key}>
              <Label className="text-slate-400 text-xs mb-2 block capitalize">{key.replace("_", " ")}</Label>
              <div className="flex flex-wrap gap-1.5">
                {opts.map((o) => (
                  <button key={o} onClick={() => pick(key, o)} data-testid={`gen-${key}-${o.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${form[key] === o ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-white/10 text-slate-400 hover:border-white/25"}`}>{o}</button>
                ))}
              </div>
            </div>
          ))}
          <div><Label className="text-slate-400 text-xs mb-2 block">Your skills</Label><Input value={form.skills} onChange={(e) => pick("skills", e.target.value)} placeholder="e.g. React, marketing" className="bg-white/5 border-white/10 text-slate-200 h-9" /></div>
          <div><Label className="text-slate-400 text-xs mb-2 block">Budget</Label><Input value={form.budget} onChange={(e) => pick("budget", e.target.value)} placeholder="e.g. $500" className="bg-white/5 border-white/10 text-slate-200 h-9" /></div>
          <div><Label className="text-slate-400 text-xs mb-2 block">Target revenue</Label><Input value={form.target_revenue} onChange={(e) => pick("target_revenue", e.target.value)} placeholder="e.g. $10k MRR" className="bg-white/5 border-white/10 text-slate-200 h-9" /></div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-white/8">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Generate:</span>
            {[5, 10, 25].map((n) => (
              <button key={n} onClick={() => setCount(n)} className={`px-3 py-1.5 rounded-lg text-sm border ${count === n ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-white/10 text-slate-400"}`} data-testid={`gen-count-${n}`}>{n}</button>
            ))}
          </div>
          <Button onClick={generate} disabled={loading} className="bg-gradient-to-r from-indigo-500 to-cyan-500 text-slate-950 font-semibold w-full sm:w-auto" data-testid="generate-ideas-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating {count} ideas…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate {count} Opportunities</>}
          </Button>
        </div>
      </div>

      {ideas.length > 0 && (
        <div className="space-y-4 fade-up">
          {ideas.map((idea, i) => (
            <div key={i} className="glass rounded-xl p-5 card-hover" data-testid={`generated-idea-${i}`}>
              <div className="flex items-start gap-4">
                <div className="text-center shrink-0">
                  <div className="w-12 h-12 rounded-xl border flex items-center justify-center" style={{ borderColor: `${scoreColor(idea.overall_score)}55`, background: `${scoreColor(idea.overall_score)}15` }}>
                    <span className="font-mono font-bold" style={{ color: scoreColor(idea.overall_score) }}>{idea.overall_score}</span>
                  </div>
                  <span className="text-[9px] uppercase text-slate-600">Rank #{i + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-mono uppercase text-cyan-400">{idea.category}</span>
                    <span className="text-[11px] font-mono text-emerald-400">{idea.revenue_potential}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{idea.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{idea.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs font-mono">
                    <Mini label="Demand" v={idea.scores?.demand} /><Mini label="Comp" v={idea.scores?.competition} invert />
                    <Mini label="Money" v={idea.scores?.monetization} /><Mini label="Build" v={idea.scores?.build_difficulty} invert />
                    <span className="text-slate-500">MVP: {idea.mvp_time}</span><span className="text-slate-500">{idea.pricing}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2"><span className="text-slate-400">Why now:</span> {idea.why_now}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" onClick={() => toProject(idea)} className="bg-cyan-500 text-slate-950" data-testid={`idea-save-${i}`}><FolderPlus className="w-3.5 h-3.5 mr-1.5" />Save as Project</Button>
                    <Button size="sm" variant="outline" onClick={() => navigate("/build-planner", { state: { description: idea.description } })} className="border-white/15 text-slate-300"><Hammer className="w-3.5 h-3.5 mr-1.5" />Build Plan</Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, v = 0, invert }) {
  return <span style={{ color: scoreColor(invert ? 100 - v : v) }}>{label} {v}</span>;
}
